/**
 * The guest GraphQL operations, as one typed object. Same names as Kalligo's generated SDK so the guest services read
 * the same; each call goes through graphqlRequest (project in the x-project-id header, never in the variables).
 */
import { graphqlRequest } from './graphqlClient';

const GUEST_FIELDS = `
  id project_id reference_id name email phone avatar status rsvp_status rsvp_status_derived rsvp_date last_login_at
  notes side dietary_restrictions household_id role is_honoree party_of party_role
  tags { id label color category }
  plus_ones { id name relationship dietary_restrictions age }
  created_at updated_at`;

export interface GuestRow {
  id: string;
  project_id: string | null;
  reference_id?: number | null;
  name: string;
  email: string;
  phone: string | null;
  avatar: string | null;
  status: string;
  rsvp_status: string;
  rsvp_status_derived?: string | null;
  rsvp_date: string | null;
  last_login_at: string | null;
  notes: string | null;
  side: string | null;
  dietary_restrictions: string[];
  household_id: string | null;
  role: string | null;
  is_honoree: boolean | null;
  party_of: string | null;
  party_role: string | null;
  tags: Array<{ id: string; label: string; color: string; category: string }>;
  plus_ones: Array<{ id: string; name: string | null; relationship: string; dietary_restrictions: string[] | null; age: number | null }>;
  created_at: string;
  updated_at: string;
}

export interface GuestStatsRow {
  total_guests: number;
  confirmed: number;
  pending: number;
  declined: number;
  other_status: number;
  total_plus_ones: number;
}

export const sdk = {
  Guests: () => graphqlRequest<{ guests: GuestRow[] }>(`query Guests { guests { ${GUEST_FIELDS} } }`),
  ArchivedGuests: () => graphqlRequest<{ guests: GuestRow[] }>(`query ArchivedGuests { guests(archived: true) { ${GUEST_FIELDS} } }`),
  Guest: (v: { ref: string }) => graphqlRequest<{ guest: { id: string; invite_code: string | null } | null }>(
    `query Guest($ref: ID!) { guest(ref: $ref) { id invite_code } }`, v),
  GuestStats: () => graphqlRequest<{ guestStats: GuestStatsRow }>(
    `query GuestStats { guestStats { total_guests confirmed pending declined other_status total_plus_ones } }`),
  DeleteGuest: (v: { ref: string }) => graphqlRequest<{ deleteGuest: boolean }>(`mutation DeleteGuest($ref: ID!) { deleteGuest(ref: $ref) }`, v),
  RestoreGuest: (v: { ref: string }) => graphqlRequest<{ restoreGuest: { id: string } }>(`mutation RestoreGuest($ref: ID!) { restoreGuest(ref: $ref) { id } }`, v),
  DeleteGuestPermanent: (v: { ref: string }) => graphqlRequest<{ deleteGuestPermanent: boolean }>(
    `mutation DeleteGuestPermanent($ref: ID!) { deleteGuestPermanent(ref: $ref) }`, v),
  MergeGuests: (v: { keep_ref: string; lose_refs: string[] }) => graphqlRequest<{ mergeGuests: { id: string } }>(
    `mutation MergeGuests($keep_ref: ID!, $lose_refs: [ID!]!) { mergeGuests(keep_ref: $keep_ref, lose_refs: $lose_refs) { id } }`, v),
  AssignGuestToEvent: (v: { event_id: string; guest_ref: string; rsvp_status?: string }) => graphqlRequest<{ assignGuestToEvent: { rsvp_status: string } | null }>(
    `mutation AssignGuestToEvent($event_id: ID!, $guest_ref: String!, $rsvp_status: String) { assignGuestToEvent(event_id: $event_id, guest_ref: $guest_ref, rsvp_status: $rsvp_status) { rsvp_status } }`, v),
  UpdateGuestEventRsvp: (v: { event_id: string; guest_ref: string; rsvp_status: string }) => graphqlRequest<{ updateGuestEventRsvp: { rsvp_status: string } | null }>(
    `mutation UpdateGuestEventRsvp($event_id: ID!, $guest_ref: String!, $rsvp_status: String!) { updateGuestEventRsvp(event_id: $event_id, guest_ref: $guest_ref, rsvp_status: $rsvp_status) { rsvp_status } }`, v),
  RemoveGuestFromEvent: (v: { event_id: string; guest_ref: string }) => graphqlRequest<{ removeGuestFromEvent: boolean }>(
    `mutation RemoveGuestFromEvent($event_id: ID!, $guest_ref: String!) { removeGuestFromEvent(event_id: $event_id, guest_ref: $guest_ref) }`, v),
};
